// ===== Packages =====

import React, {
    useRef,
    useState,
    useEffect,
}                                       from 'react';
import {
    useNavigate,
    useLocation,
}                                       from 'react-router-dom';
import { ReactSVG }                     from 'react-svg';

// ===== Hooks =====

import {
    useTimeout,
}                                       from '../../hooks';

// ===== Enums =====

import {
    CURSOR_TARGET,
    INTERACTABLE_OBJECT,
    PAGE_ROUTE,
    USER_ACTION_TYPE,
}                                       from '../../enums';

// ===== Services =====

import {
    recordUserAction,
    updatePageTitle,
    getPressEventHandlers,
    getTouchMouseEventHandlers,
    isTestMode,
} from '../../services';

// ===== Interfaces =====

// ===== Images =====

import VerascopeLogo                    from '../../images/verascope-logo-silhouette.svg';

// ===== Constants =====

import {
    HOVER_TARGET_CLASSNAME,
    PAGE_SUBTITLE_TRANSITION_DURATION,
}                                       from '../../constants/generalConstants';
import CURSOR_SIGN                      from '../../constants/cursorSigns';

// ===== Styles =====

import {
    Container,
    AboutText,
    AboutTextContainer,
    ColorText,
}                                       from './styles';
import {
    PageLogo,
    PageSubtitle,
    PageTitle,
}                                       from '../../styles';
import { theme }                        from '../../themes/theme-context';

interface Props {
    customCursorActive: boolean,
    currentSessionId: string | null,
    onCursorEnter: (
        targetType: CURSOR_TARGET | INTERACTABLE_OBJECT | string,
        actions: string[],
        candidateTarget?: HTMLElement,
    ) => void,
    onCursorLeave: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
}
function AboutView({
    customCursorActive,
    currentSessionId,
    onCursorEnter,
    onCursorLeave,
}: Props): JSX.Element {
    // ===== React Router =====

    const navigate = useNavigate();
    const location = useLocation();

    // ===== State =====

    const [showText, setShowText] = useState<boolean>(false);
    // Stores the location key for which a view action has been recorded.
    const recordedViewPageUserActionKey = useRef<string | null>(null);

    // ===== Animation Constants =====

    const ABOUT_TEXT_DURATION = 400;
    const HEADER_DELAY_DURATION = PAGE_SUBTITLE_TRANSITION_DURATION - 150;

    // ===== Methods =====

    const onLogoEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.logomark,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onLogoLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    const onLogoClick = async (e: React.MouseEvent): Promise<void> => {
        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.clickHomeButton,
                sessionId: currentSessionId,
                payload: {
                    route: PAGE_ROUTE.about,
                },
            });
        }
        onCursorLeave(e);
        navigate(
            `/${PAGE_ROUTE.landing}`,
            {
                state: {
                    prevPath: location.pathname,
                },
            },
        );
    };

    /**
     * Records user action of viewing about page
     */
    const recordAboutPageUserAction = async (): Promise<void> => {
        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.viewAboutPage,
                sessionId: currentSessionId,
            });
        }
    };

    // ===== Side Effects =====

    /**
     * Manages page title changes
     */
    useEffect(() => {
        updatePageTitle(
            'About',
        );
    }, []);

    /**
     * Records View About Page User Action
     */
    useEffect(() => {
        if (
            !currentSessionId
            || recordedViewPageUserActionKey.current === location.key
        ) return;
        recordAboutPageUserAction();
        recordedViewPageUserActionKey.current = location.key;
    }, [
        currentSessionId,
        location.key,
    ]);

    // Show text
    useEffect(() => {
        clearTimeoutShowText();
        timeoutShowText();
    }, []);

    const {
        start: timeoutShowText,
        clear: clearTimeoutShowText,
    } = useTimeout(() => {
        setShowText(true);
    }, HEADER_DELAY_DURATION);

    return (
        <Container>
            <PageLogo
                withEnterTransition
                className={HOVER_TARGET_CLASSNAME}
                customCursorActive={customCursorActive}
                {...getTouchMouseEventHandlers({
                    onMouseEnter: (e) => onLogoEnter(e),
                    onMouseLeave: (e) => onLogoLeave(e),
                })}
                {...getPressEventHandlers({ onMouseDown: onLogoClick })}
            >
                <ReactSVG
                    src={VerascopeLogo}
                />
            </PageLogo>
            <PageTitle
                withEnterTransition
            >
                ABOUT
            </PageTitle>
            <PageSubtitle
                withEnterTransition
                color={theme.verascopeColor.orange200}
            >
                What is verascope?
            </PageSubtitle>
            <AboutTextContainer>
                <AboutText
                    isVisible={showText}
                    delay={0}
                    enterDuration={ABOUT_TEXT_DURATION}
                >
                    verascope explores the use of
                    <ColorText color={theme.verascopeColor.orange300}> video games </ColorText>
                    to improve how
                    <ColorText color={theme.verascopeColor.orange300}> humans learn </ColorText>
                    and how STEM subjects are taught.
                </AboutText>
                <AboutText
                    marginTop
                    isVisible={showText}
                    delay={ABOUT_TEXT_DURATION / 2}
                    enterDuration={ABOUT_TEXT_DURATION}
                >
                    We create
                    <ColorText color={theme.verascopeColor.orange300}> gamemaking software </ColorText>
                    for advanced-level STEM educators that enables them to
                    <ColorText color={theme.verascopeColor.orange300}> adapt complex, abstract subjects </ColorText>
                    (like linear algebra or quantum physics) into the form of fun, accessible video games that can carry out
                    <ColorText color={theme.verascopeColor.orange300}> teaching through engaging gameplay </ColorText>
                    as opposed to traditional lecture-based or textbook-based approaches.
                </AboutText>
                <AboutText
                    marginTop
                    isVisible={showText}
                    delay={ABOUT_TEXT_DURATION}
                    enterDuration={ABOUT_TEXT_DURATION}
                >
                    Our mission is to
                    <ColorText color={theme.verascopeColor.orange300}> cultivate individuals </ColorText>
                    with
                    <ColorText color={theme.verascopeColor.orange300}> high agency</ColorText>
                    , capable of effectively navigating a complex world.
                </AboutText>
            </AboutTextContainer>
        </Container>
    );
}

export default AboutView;
